// src/prototype/hooks/useEvaluationRun.ts
// ============================================================
// EVALUATION-RUN GATE
//
// Entering Evaluate for the first time after a personalization change shows a short processing
// sequence. The point is narrative honesty: the presenter has just changed the inputs, and the
// ranking on the next screen genuinely is a different computation. Showing it happen — once —
// makes that legible. Showing it EVERY time would be theatre, so a flag in demo state arms it
// on change and disarms it on view.
//
// WHAT IT DOES NOT DO: no fake percentage, no progress bar, no invented elapsed time. Four
// stage labels appear on a fixed schedule and that is all. Each label names something the
// system actually does, and the counts in them (174 use cases, 38 measures) are the real ones.
//
// Deterministic timing — no Math.random — so every rehearsal matches every delivery.
// ============================================================

import { useCallback, useEffect, useState } from 'react';

export const EVAL_RUN_PENDING_KEY = 'dva_demo_eval_run_pending';
export const EVAL_RUN_CHANGE_EVENT = 'dva_demo_eval_run_change';

/** Total run time, and when each stage label appears. Sums to ~5s. */
export const EVAL_STAGE_MS = 1250;
export const EVAL_TOTAL_MS = EVAL_STAGE_MS * 4;

export const EVAL_STAGES: readonly string[] = [
  'Triaging 174 use cases',
  'Scoring 38 measures per use case',
  "Applying ACME's personalization profile",
  'Ranking the portfolio',
] as const;

function read(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(EVAL_RUN_PENDING_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Arm the interstitial. Call from every surface that changes personalization inputs:
 * document upload/delete, initiatives import, profile save, weight edits.
 */
export function markPersonalizationChanged(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(EVAL_RUN_PENDING_KEY, '1');
  } catch {
    return;
  }
  window.dispatchEvent(new CustomEvent(EVAL_RUN_CHANGE_EVENT, { detail: true }));
}

/** Disarm — the run has been shown (or skipped). */
export function clearPersonalizationChanged(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(EVAL_RUN_PENDING_KEY);
  } catch {
    return;
  }
  window.dispatchEvent(new CustomEvent(EVAL_RUN_CHANGE_EVENT, { detail: false }));
}

export interface EvaluationRunApi {
  /** True while the interstitial should be on screen. */
  isRunning: boolean;
  /** How many stage labels are visible (0-4). */
  visibleStages: number;
  /** Dismiss immediately — the whole overlay is click-to-skip. */
  skip: () => void;
}

/**
 * Drives the interstitial on the Evaluate surface.
 * Starts only when the pending flag is set; clears the flag as soon as it starts, so a refresh
 * mid-run does not replay it.
 */
export function useEvaluationRun(): EvaluationRunApi {
  const [isRunning, setIsRunning] = useState(() => read());
  const [visibleStages, setVisibleStages] = useState(() => (read() ? 1 : 0));

  useEffect(() => {
    if (!isRunning) return undefined;
    // Consume the flag up front: shown once per personalization change.
    clearPersonalizationChanged();

    const timers: number[] = [];
    for (let i = 1; i < EVAL_STAGES.length; i += 1) {
      timers.push(window.setTimeout(() => setVisibleStages(i + 1), EVAL_STAGE_MS * i));
    }
    timers.push(window.setTimeout(() => setIsRunning(false), EVAL_TOTAL_MS));
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [isRunning]);

  // The presenter reset re-arms it, so the next Evaluate entry replays the beat.
  useEffect(() => {
    const onReset = () => {
      setIsRunning(false);
      setVisibleStages(0);
    };
    window.addEventListener('ea_demo_reset', onReset);
    return () => window.removeEventListener('ea_demo_reset', onReset);
  }, []);

  const skip = useCallback(() => {
    setIsRunning(false);
    clearPersonalizationChanged();
  }, []);

  return { isRunning, visibleStages, skip };
}

export default useEvaluationRun;
